<template>
	<Combobox
		v-model="selectedValue"
		class="relative"
		:class="comboBoxWidth"
		:disabled="isDisabled"
		as="div"
	>
		<div
			class="relative"
			:class="{ 'text-won-gray-light' : isDisabled }"
		>
			<ComboboxLabel
				v-if="typeof label === 'string'"
			>
				{{ label }}
			</ComboboxLabel>

			<ComboboxInput
				:class="[inputStyle, inputClasses]"
				class="h-auto! pr-5 leading-5"
				:placeholder="placeholder"
				:disabled="disableTyping"
				:display-value="(item) => item?.[displayKey] || item"
				@change="query = $event.target.value"
			/>

			<ComboboxButton 
				class="absolute inset-y-0 right-1 flex w-full items-center justify-end"
			>
				<ChevronDownIcon
					class="size-4"
				/>
			</ComboboxButton>
		</div>

		<ComboboxOptions
			as="div"
			class="
				absolute z-9999 mt-0.5 w-full rounded border border-won-gray-light
				bg-white px-2.5 py-1.5 shadow-float-select
			"
			:class="[
				disableOverflow ? '':'overflow-y-scroll',
				enableDropUpClasses ? 'bottom-11' : '',
				optionsContainerHeight
			]"
		>
			<div
				v-if="filteredOptions.length === 0 && query !== ''"
				class="relative cursor-default px-2 py-1.5 text-base text-won-dark-blue select-none"
			>
				Nothing found
			</div>

			<ComboboxOption
				v-for="option in filteredOptions"
				v-else
				:key="option?.value || option"
				as="div"
				:value="option?.display ? (option?.value || option) : option"
				class="cursor-pointer px-2 py-1.5 hover:bg-won-sand-light"
				:class="inputClasses"
			>
				<slot
					name="menuOption"
					:option="option"
				>
					<span
						class="text-base text-won-dark-blue"
						v-html="option?.display || option"
					/>
				</slot>
			</ComboboxOption>
		</ComboboxOptions>
	</Combobox>
</template>

<script setup>
import { ref, toRefs, computed, watch } from 'vue';
import { ChevronDownIcon } from '@heroicons/vue/24/solid';
import {
	Combobox, ComboboxButton,
	ComboboxInput,
	ComboboxOption,
	ComboboxOptions,
	ComboboxLabel
} from '@headlessui/vue';
import { inputStyle } from '../../utils/styles';
import { escapeRegExp, sanitizeHtmlText } from '../../utils/helpers';



const emit = defineEmits(['value:update']);
const props = defineProps({
	options: {
		type: Array,
		default: () => []
	},
	defaultValue: {
		type: [String, Object],
		default: null
	},
	placeholder: {
		type: String,
		default: 'Type to search'
	},
	comboBoxWidth: {
		type: String,
		default: ''
	},
	label: {
		type: String,
		default: null
	},
	highlightQuery: {
		type: Boolean,
		default: false
	},
	disableTyping: {
		type: Boolean,
		default: false
	},
	disableOverflow: {
		type: Boolean,
		default: false
	},
	enableDropUpClasses: {
		type: Boolean,
		default: false
	},
	isDisabled: {
		type: Boolean,
		default: false
	},
	optionsContainerHeight: {
		type: String,
		default: 'max-h-[150px]'
	},
	inputClasses: {
		type: String,
		default: ''
	},
	displayKey: {
		type: String,
		default: 'value'
	}
});

const { defaultValue, options, highlightQuery } = toRefs(props);

const query = ref(null);
const selectedValue = ref(defaultValue.value);

const highlightMatchIn = (str, matchObject) => str.replace(matchObject[0], `<span class="query-match">${matchObject[0]}</span>`);

const filteredOptions = computed(() => {
	if (query.value == null || query.value === '') {
		return options.value;
	}

	const searchStr = new RegExp(escapeRegExp(query.value), 'i');
	const searchResult = [];

	options.value.forEach((val) => {
		const match = searchStr.exec(val);

		if (match != null) {
			if (highlightQuery.value) {
				searchResult.push({
					display: highlightMatchIn(val, match),
					value: val
				});
			} else {
				searchResult.push(val);
			}
		}
	});

	return searchResult;
});

watch(selectedValue, (newValue) => {
	emit('value:update', typeof newValue === 'object' ? newValue : sanitizeHtmlText(newValue));
});
</script>

<style>
.query-match {
    text-decoration: underline;
    font-weight: bold;
}
</style>
